import { useMemo, useState } from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProductType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import vconst from 'utils/helpers/structs/vconst';
import FormSelect from 'components/forms/FormSelect';

interface ProductTitleProps {
    product?: ProductType | Partial<ProductType> | null;
    fetchProducts?: (force?: boolean) => Promise<unknown>;
    onClick?: () => void;
}

const ProductTitle = ({ product, fetchProducts, onClick }: ProductTitleProps) => {
    const { t } = useTranslation(['Products']);
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const { showToast } = useToast();
    const [isVendorEditing, setIsVendorEditing] = useState(false);
    const canEditVendor = hasUserAccess(auth?.user, 'prodEdit');
    const canInlineEditVendor = canEditVendor && typeof fetchProducts === 'function' && Number(product?.ID || 0) > 0;

    const vendorOptions = useMemo(
        () =>
            (localSettings?.DIRS?.Vendor || [])
                .filter((vendor) => !vendor.hidden || vendor.ID === product?.vendorId)
                .map((vendor) => ({
                    value: vendor.ID,
                    label: vendor.vendorName,
                })),
        [localSettings?.DIRS?.Vendor, product?.vendorId],
    );

    if (!product) {
        return (
            <Col>
                <h6 className="mb-1 text-muted">{t('messProductNotSet', { defaultValue: '—' })}</h6>
            </Col>
        );
    }

    const handleSaveVendor = async (value: string | number | null) => {
        if (!canInlineEditVendor || !product?.ID) return;
        const vendorId = value === null || value === '' ? null : Number(value);
        const stat = await apiProduct.saveVendor(product.ID, vendorId);
        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setIsVendorEditing(false);
        await fetchProducts?.(true);
    };

    return (
        <Col>
            <h6 className="mb-1 cursor-pointer" onClick={() => (onClick ? onClick() : null)}>
                {product.prodStatus === vconst.prodArchive && (
                    <i className="bi bi-archive-fill fs-14 text-danger me-1" title={t('titleArchive')}></i>
                )}
                {product.prodName || '—'}
            </h6>
            {isVendorEditing && canInlineEditVendor ? (
                <div className="mt-1">
                    <FormSelect
                        compact
                        defaultValue={product?.vendorId}
                        options={vendorOptions}
                        emptyOptionLabel={t('messVendorNotSet')}
                        setShowForm={setIsVendorEditing}
                        callBack={handleSaveVendor}
                    />
                </div>
            ) : (
                <p
                    className={`f-12 mb-0 ${canInlineEditVendor ? 'text-primary product-vendor-editable' : 'text-primary'}`}
                    role={canInlineEditVendor ? 'button' : undefined}
                    tabIndex={canInlineEditVendor ? 0 : undefined}
                    onClick={() => {
                        if (canInlineEditVendor) setIsVendorEditing(true);
                    }}
                    onKeyDown={(e) => {
                        if (!canInlineEditVendor) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsVendorEditing(true);
                        }
                    }}
                >
                    {product?.vendorName || t('messVendorNotSet')}
                </p>
            )}
        </Col>
    );
};

export default ProductTitle;
